import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate }                                 from 'react-router-dom';
import { ClockIcon, TrashIcon }                        from '@heroicons/react/outline';
import {
  Checkbox,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';

import {
	requestDismissMultipleReminders,
	requestDismissReminder,
	requestUpdateReminder
}                                     from '@modules/ContactManager/components/Header/requests';
import { getMoment, withQueryParams } from '@shared/commonFunctions';
import { AppPopover }                 from '@shared/components/AppPopover';
import { useAnchor }                  from '@shared/hooks/useAnchor';
import { EDismissButtonTypes }        from '@shared/interfaces';
import { dateFormatWithTime }         from '@shared/constants/formats';
import { ButtonWrapper, ReminderDetails, ReminderFooter, ReminderRow, ReminderRowGroup } from '@styles/Layout/PopOversStyles';
import { useTabs } from '@shared/hooks/useTabs';
import { reminderUrlByKey } from '@config/routesConfig';

import { HeaderControlButton } from '../HeaderControlButton';

export interface IReminders {
  id: string;
  userId: string;
  contactId: string;
  contactNumericId: number;
  contactFirstName: string;
  contactLastName: string;
  dealId: string;
  reminderDate: string;
  description: string;
  actionDate: string;
  isEmailReminder: boolean;
  isEmailSent: boolean;
}

interface IRemindersProps {
  reminders: IReminders[];
  reloadReminders: () => void;
}

export const RemindersPreview = ({ reminders, reloadReminders }: IRemindersProps) => {
  const theme = useTheme();
  const [anchor, toggleAnchor] = useAnchor();
  const { openTab } = useTabs();

  const navigate = useNavigate();

  const [snoozeTime, setSnoozeTime] = useState<{ [key: string]: string | object }>({});
  const [selectedReminders, setSelectedReminders] = useState<string[]>([]);

  const dismissButtonTitle = useMemo(
    () => (selectedReminders.length ? EDismissButtonTypes.DismissSelected : EDismissButtonTypes.DismissAll),
    [selectedReminders],
  );

  const handleChange = useCallback(
    async(e: SelectChangeEvent<string | object>, reminderId: string) => {
      await setSnoozeTime((prev) => ({ ...prev, [reminderId as string]: e.target.value }));
      let date: Date | string = new Date();
      date = date.toISOString();
      const newTime = getMoment(date).add(Number(e.target.value), 'minutes');
      await requestUpdateReminder(reminderId, newTime.toISOString());
      reloadReminders();
    },
    [requestUpdateReminder],
  );

  const onReminderClick = useCallback((row: IReminders) => {
    const route = withQueryParams('/contact-manager', { id: row.contactId, tab: 0 }, false);
    const title = `${row.contactFirstName} ${row.contactLastName}`;
    openTab({ title, route });
    navigate(route);
    toggleAnchor();
  }, []);

  const onViewAllClick = useCallback((url: string) => {
    openTab({ title: 'Reminders', route: url });
    navigate(url);
    toggleAnchor();
  }, []);
  const reminderData = useMemo<IReminders[]>(() => reminders?.slice(0, 4), [reminders]);

  const onCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>, id: string) => {
    if (event.target.checked) {
      setSelectedReminders((prev) => [...prev, id]);
    } else {
      setSelectedReminders((prev) => prev.filter((reminderId) => reminderId !== id));
    }
  }, []);

	const onDismissButtonClick = useCallback(async() => {
    if (reminders.length) {
      if (selectedReminders.length) {
        await requestDismissMultipleReminders({ reminderIds: selectedReminders });
      } else {
        await requestDismissMultipleReminders({ reminderIds: reminders.map((reminder) => reminder.id)});
      }

      setSelectedReminders([]);
      reloadReminders();
    }
	}, [selectedReminders, reminders, requestDismissMultipleReminders, reloadReminders]);

  const onToggleAnchor = useCallback(() => {
    toggleAnchor();
    setSelectedReminders([]);
  }, [toggleAnchor]);

	return (
		<>
			<AppPopover title="Phoenix Reminders" anchor={anchor} toggleAnchor={onToggleAnchor}>
				{reminders && (
					<ButtonWrapper isAllBtn={dismissButtonTitle === EDismissButtonTypes.DismissAll} onClick={onDismissButtonClick}>{dismissButtonTitle}</ButtonWrapper>
				)}

					{reminderData?.map((item: IReminders, index: number) => (
						<ReminderRow key={index}>
							<Checkbox checked={selectedReminders.includes(item.id)} onChange={(e) => onCheckboxChange(e, item.id)} />
							<ReminderDetails onClick={() => onReminderClick(item)}>
								<Typography variant="subtitle1">{`${item?.contactFirstName} ${item.contactLastName} (${item.contactNumericId})`}</Typography>
								<Typography variant="caption">{getMoment(item?.reminderDate.toString()).format(dateFormatWithTime)}</Typography>
							</ReminderDetails>

            <ReminderRowGroup>
              <FormControl fullWidth>
                <Select
                  autoWidth
                  displayEmpty
                  labelId  = "snooze-select-label"
                  value    = {snoozeTime[item?.id as string] || ''}
                  label    = "Snooze"
                  id       = "snooze-label"
                  onChange = {(e) => handleChange(e, item?.id)}
                >
                  <MenuItem sx={{ display: 'none' }} value={''}>
                    Snooze
                  </MenuItem>
                  <MenuItem value={5}>5 minutes</MenuItem>
                  <MenuItem value={10}>10 minutes</MenuItem>
                  <MenuItem value={30}>30 minutes</MenuItem>
                  <MenuItem value={60}>1 hour</MenuItem>
                </Select>
                <IconButton
                  onClick={async() => {
                    await requestDismissReminder(item?.id);
                    reloadReminders();
                  }}
                >
                  <TrashIcon width={24} color={theme.palette.secondary.dark} />
                </IconButton>
              </FormControl>
            </ReminderRowGroup>
          </ReminderRow>
        ))}

        <ReminderFooter onClick={() => onViewAllClick(reminderUrlByKey['Reminders']?.url ?? '')}>View All</ReminderFooter>
      </AppPopover>
                  
      <HeaderControlButton count={reminders?.length} icon={ClockIcon} onClick={toggleAnchor} />
    </>
  );
};
